/**
 * Growing a hashtree: BUD-16 directories and BUD-17 file chunking / fanout.
 *
 * Every node in this app is CHK-encrypted, so building a node always means:
 *
 *   links -> deterministic MessagePack -> encryptCHK -> {blobHash, chkKey}
 *
 * and the parent then links to it with `h = blobHash` and `k = chkKey`. That
 * `k`-in-the-parent pattern is what lets a whole encrypted tree hang off one
 * root hash and one root key.
 */

import { formatBytes } from "./bytes";
import { encryptCHK, type EncryptedBlob } from "./chk";
import {
  LinkTypeBlob,
  NodeType,
  encodeNode,
  sortDirectoryLinks,
  type Link,
  type LinkType,
  type Metadata,
  type NodeTypeValue,
} from "./msgpack";

/** BUD-17 canonical constants. Both are writer-side choices, not reader requirements. */
export const CANONICAL_CHUNK_SIZE = 2 * 1024 * 1024; // 2 MiB
export const CANONICAL_MAX_LINKS = 174;

export interface SplitOptions {
  /** Maximum plaintext bytes per leaf chunk. */
  chunkSize: number;
  /** Maximum links in a file or directory node before it has to nest. */
  maxLinks: number;
}

export const CANONICAL_SPLIT: SplitOptions = {
  chunkSize: CANONICAL_CHUNK_SIZE,
  maxLinks: CANONICAL_MAX_LINKS,
};

// ---------------------------------------------------------------------------
// Built tree representation
// ---------------------------------------------------------------------------

export type NodeKind = "blob" | "file" | "directory" | "fanout";

export interface BuiltNode {
  /** Stable identity for React keys and inspector selection. */
  id: string;
  kind: NodeKind;
  /** Human label for the inspector, e.g. a file name or `file manifest`. */
  label: string;
  /** `undefined` for a raw blob, which has no node type of its own. */
  nodeType?: NodeTypeValue;
  /** For manifests: the MessagePack bytes. For blobs: the chunk plaintext. */
  plaintext: Uint8Array;
  encrypted: EncryptedBlob;
  /** Links this node contains. Empty for a raw blob. */
  links: Link[];
  /** Resolved children, in link order, so the inspector can walk the tree. */
  children: BuiltNode[];
  /** Plaintext bytes this node represents — the `s` a parent link would use. */
  size: number;
}

/** The link type a parent should use when pointing at this node. */
export function linkTypeOf(node: BuiltNode): LinkType {
  switch (node.kind) {
    case "blob": return LinkTypeBlob;
    case "file": return NodeType.File;
    case "directory": return NodeType.Directory;
    case "fanout": return NodeType.Fanout;
  }
}

/** Build the parent link that points at an already-built node. */
export function linkTo(node: BuiltNode, options: { name?: string; metadata?: Metadata } = {}): Link {
  return {
    h: node.encrypted.blobHash,
    k: node.encrypted.chkKey,
    ...(options.metadata ? { m: options.metadata } : {}),
    ...(options.name !== undefined ? { n: options.name } : {}),
    s: node.size,
    t: linkTypeOf(node),
  };
}

/** Every blob in the tree that has to be uploaded, deepest first. */
export function collectBlobs(root: BuiltNode): BuiltNode[] {
  const out: BuiltNode[] = [];
  const walk = (node: BuiltNode) => {
    for (const child of node.children) walk(child);
    out.push(node);
  };
  walk(root);
  return out;
}

let nodeCounter = 0;
const nextId = (prefix: string) => `${prefix}-${nodeCounter++}`;

/** Reset ids so a rebuild from the same input produces the same ids. */
export function resetNodeIds(): void {
  nodeCounter = 0;
}

async function encryptNode(
  kind: NodeKind,
  nodeType: NodeTypeValue,
  label: string,
  links: Link[],
  children: BuiltNode[],
  size: number,
): Promise<BuiltNode> {
  const plaintext = encodeNode({ l: links, t: nodeType });
  return {
    id: nextId(kind),
    kind,
    label,
    nodeType,
    plaintext,
    encrypted: await encryptCHK(plaintext),
    links,
    children,
    size,
  };
}

// ---------------------------------------------------------------------------
// BUD-17: splitting a file
// ---------------------------------------------------------------------------

export interface ChunkRange {
  index: number;
  start: number;
  /** Exclusive. */
  end: number;
  size: number;
}

export type FileShape =
  /** One chunk: BUD-17 allows publishing it as a plain blob with no manifest at all. */
  | { kind: "single-blob"; chunkCount: 1 }
  /** Chunks fit in one manifest. */
  | { kind: "flat-manifest"; chunkCount: number }
  /** Too many chunks for one node, so `t = 1` manifests nest. */
  | { kind: "nested-manifests"; chunkCount: number; depth: number; manifestCount: number };

export interface FileGeometry {
  fileSize: number;
  chunkSize: number;
  maxLinks: number;
  chunks: ChunkRange[];
  shape: FileShape;
  /** Total blobs this file contributes to the tree: chunks plus manifests. */
  blobCount: number;
  /** Exact byte size of every manifest this file needs, summed. */
  manifestBytes: number;
  /** One-line description of the shape, for the chunk size preview. */
  summary: string;
}

/** Split a byte length into consecutive chunk ranges. Never hashes anything. */
export function chunkRanges(fileSize: number, chunkSize: number): ChunkRange[] {
  if (chunkSize < 1) throw new Error("chunk size must be at least 1 byte");
  if (fileSize === 0) return [{ index: 0, start: 0, end: 0, size: 0 }];

  const ranges: ChunkRange[] = [];
  for (let start = 0, index = 0; start < fileSize; start += chunkSize, index++) {
    const end = Math.min(start + chunkSize, fileSize);
    ranges.push({ index, start, end, size: end - start });
  }
  return ranges;
}

/**
 * Exact encoded size of a manifest with `linkCount` links, without building one.
 *
 * We encode a stand-in node with correctly sized dummy values: hashes and keys
 * are always 32 bytes, so only the `s` integers vary, and we know those.
 */
function measureManifest(nodeType: NodeTypeValue, sizes: number[], named: string[] | null): number {
  const links: Link[] = sizes.map((s, index) => ({
    h: new Uint8Array(32),
    k: new Uint8Array(32),
    ...(named ? { n: named[index] ?? "" } : {}),
    s,
    t: nodeType === NodeType.File ? LinkTypeBlob : NodeType.Directory,
  }));
  return encodeNode({ l: links, t: nodeType }).length;
}

/**
 * Work out the shape of a file at a given chunk size, cheaply enough to run on
 * every frame of a slider drag. This mirrors `buildFileNode` exactly.
 */
export function planFile(fileSize: number, { chunkSize, maxLinks }: SplitOptions): FileGeometry {
  if (maxLinks < 2) throw new Error("max links must be at least 2");

  const chunks = chunkRanges(fileSize, chunkSize);
  const chunkCount = chunks.length;

  // BUD-17 step 1: a file that fits in one chunk needs no manifest.
  if (chunkCount === 1) {
    return {
      fileSize,
      chunkSize,
      maxLinks,
      chunks,
      shape: { kind: "single-blob", chunkCount: 1 },
      blobCount: 1,
      manifestBytes: 0,
      summary: `1 chunk — published as a single blob, with no file manifest at all`,
    };
  }

  // Level 0 is the chunk layer. Group upward until one node holds everything.
  let levelSizes = chunks.map((chunk) => chunk.size);
  let manifestCount = 0;
  let manifestBytes = 0;
  let depth = 0;
  let nodeType: NodeTypeValue = NodeType.File;

  while (levelSizes.length > 1) {
    const parents: number[] = [];
    for (let i = 0; i < levelSizes.length; i += maxLinks) {
      const group = levelSizes.slice(i, i + maxLinks);
      manifestBytes += measureManifest(nodeType, group, null);
      manifestCount++;
      parents.push(group.reduce((sum, size) => sum + size, 0));
    }
    levelSizes = parents;
    depth++;
  }

  const shape: FileShape =
    depth === 1
      ? { kind: "flat-manifest", chunkCount }
      : { kind: "nested-manifests", chunkCount, depth, manifestCount };

  const summary =
    depth === 1
      ? `${chunkCount} chunks — one flat t=1 file manifest`
      : `${chunkCount} chunks — ${manifestCount} t=1 manifests nested ${depth} levels deep`;

  return {
    fileSize,
    chunkSize,
    maxLinks,
    chunks,
    shape,
    blobCount: chunkCount + manifestCount,
    manifestBytes,
    summary,
  };
}

/**
 * Build the encrypted file subtree.
 *
 * Returns a raw blob node when the file fits in one chunk, otherwise the root
 * `t = 1` manifest. Follows BUD-17's splitting steps in order.
 */
export async function buildFileNode(
  bytes: Uint8Array,
  fileName: string,
  { chunkSize, maxLinks }: SplitOptions,
): Promise<BuiltNode> {
  if (maxLinks < 2) throw new Error("max links must be at least 2");

  const ranges = chunkRanges(bytes.length, chunkSize);

  // Steps 2-3: one leaf blob per chunk.
  const leaves: BuiltNode[] = [];
  for (const range of ranges) {
    const plaintext = bytes.slice(range.start, range.end);
    leaves.push({
      id: nextId("blob"),
      kind: "blob",
      label: ranges.length === 1 ? fileName : `chunk ${range.index}`,
      plaintext,
      encrypted: await encryptCHK(plaintext),
      links: [],
      children: [],
      size: plaintext.length,
    });
  }

  // Step 1: a single chunk needs no manifest.
  if (leaves.length === 1) return leaves[0]!;

  // Steps 4-6: group into `t = 1` manifests until one node holds everything.
  let level = leaves;
  while (level.length > 1) {
    const parents: BuiltNode[] = [];
    for (let i = 0; i < level.length; i += maxLinks) {
      const group = level.slice(i, i + maxLinks);
      const links = group.map((child) => linkTo(child)); // no `n` in a file manifest
      const size = group.reduce((sum, child) => sum + child.size, 0);
      parents.push(await encryptNode("file", NodeType.File, "file manifest", links, group, size));
    }
    level = parents;
  }

  const root = level[0]!;
  root.label = `${fileName} (file manifest)`;
  return root;
}

// ---------------------------------------------------------------------------
// BUD-16: directory entry names
// ---------------------------------------------------------------------------

/** Returns an error message, or `null` when the name is valid. */
export function validateEntryName(name: string): string | null {
  if (name.length === 0) return "Name must not be empty";
  if (name.includes("/")) return "Name must not contain `/`";
  if (name.includes("\0")) return "Name must not contain the NUL character";
  if (name === "." || name === "..") return "Name must not be `.` or `..`";
  return null;
}

export function assertUniqueNames(names: string[]): void {
  const seen = new Set<string>();
  for (const name of names) {
    const problem = validateEntryName(name);
    if (problem) throw new Error(`${problem}: ${JSON.stringify(name)}`);
    if (seen.has(name)) throw new Error(`duplicate directory entry name: ${JSON.stringify(name)}`);
    seen.add(name);
  }
}

// ---------------------------------------------------------------------------
// BUD-16 / BUD-17: building a directory, with fanout when it is too large
// ---------------------------------------------------------------------------

export interface DirectoryEntry {
  name: string;
  node: BuiltNode;
}

export type DirectoryShape =
  | { kind: "flat-directory"; entryCount: number }
  /**
   * `childCount` is the number of leaf child directories the entries split into.
   * `depth` counts the levels above the entries: the child directory level plus
   * one per fanout level needed to reduce those children to a single root.
   */
  | { kind: "fanout"; entryCount: number; depth: number; childCount: number };

/** Shape a directory would take, without building it. */
export function planDirectory(entryCount: number, { maxLinks }: Pick<SplitOptions, "maxLinks">): DirectoryShape {
  if (entryCount <= maxLinks) return { kind: "flat-directory", entryCount };

  let level = Math.ceil(entryCount / maxLinks);
  const childCount = level;
  let depth = 1;
  while (level > 1) {
    level = Math.ceil(level / maxLinks);
    depth++;
  }
  return { kind: "fanout", entryCount, depth, childCount };
}

/** The user-visible entry names beneath a node, in sorted order. */
function visibleNames(node: BuiltNode): string[] {
  if (node.kind === "directory") {
    return node.links.map((link) => link.n!).filter((name) => name !== undefined);
  }
  if (node.kind === "fanout") {
    return node.children.flatMap(visibleNames);
  }
  return [];
}

/**
 * Build an encrypted directory.
 *
 * Small directories are a single BUD-16 `t = 2` manifest. Large ones split into
 * batches of `maxLinks` child directories under BUD-17 `t = 3` fanout nodes,
 * whose internal links are unnamed and carry `count` / `first` / `last` bounds
 * so a reader can pick the right subtree without fetching all of them.
 */
export async function buildDirectoryNode(
  entries: DirectoryEntry[],
  label: string,
  { maxLinks }: Pick<SplitOptions, "maxLinks">,
): Promise<BuiltNode> {
  assertUniqueNames(entries.map((entry) => entry.name));

  // Step 1: sort user-visible entries bytewise by name.
  const links = sortDirectoryLinks(entries.map((entry) => linkTo(entry.node, { name: entry.name })));
  const byName = new Map(entries.map((entry) => [entry.name, entry.node]));
  const sorted = links.map((link) => ({ link, node: byName.get(link.n!)! }));

  // Step 2: it fits, so it is a plain BUD-16 directory.
  if (sorted.length <= maxLinks) {
    const size = sorted.reduce((sum, item) => sum + item.link.s, 0);
    return encryptNode(
      "directory",
      NodeType.Directory,
      label,
      sorted.map((item) => item.link),
      sorted.map((item) => item.node),
      size,
    );
  }

  // Steps 3-4: one child directory manifest per batch of `maxLinks` entries.
  let level: BuiltNode[] = [];
  for (let i = 0; i < sorted.length; i += maxLinks) {
    const batch = sorted.slice(i, i + maxLinks);
    const size = batch.reduce((sum, item) => sum + item.link.s, 0);
    level.push(
      await encryptNode(
        "directory",
        NodeType.Directory,
        `${label} (entries ${i}–${i + batch.length - 1})`,
        batch.map((item) => item.link),
        batch.map((item) => item.node),
        size,
      ),
    );
  }

  // Steps 5-8: cover the children with `t = 3` fanout nodes until one remains.
  while (level.length > 1) {
    const parents: BuiltNode[] = [];
    for (let i = 0; i < level.length; i += maxLinks) {
      const group = level.slice(i, i + maxLinks);
      const groupLinks = group.map((child) => {
        const names = visibleNames(child);
        return linkTo(child, {
          // Fanout links omit `n` and carry subtree bounds instead.
          metadata: { count: names.length, first: names[0]!, last: names[names.length - 1]! },
        });
      });
      const size = group.reduce((sum, child) => sum + child.size, 0);
      parents.push(await encryptNode("fanout", NodeType.Fanout, `${label} (fanout)`, groupLinks, group, size));
    }
    level = parents;
  }

  const root = level[0]!;
  root.label = label;
  return root;
}

/** `2 MiB` style label with a note when the value is the canonical one. */
export function describeChunkSize(chunkSize: number): string {
  return chunkSize === CANONICAL_CHUNK_SIZE ? `${formatBytes(chunkSize)} (canonical)` : formatBytes(chunkSize);
}
